import React, { useState } from 'react';

const TEAM_COLORS = {
  'Red Bull Racing':  '#3671C6',
  'Ferrari':          '#E8002D',
  'Mercedes':         '#00D2BE',
  'McLaren':          '#FF8000',
  'Aston Martin':     '#229971',
  'Alpine':           '#FF87BC',
  'Williams':         '#64C4FF',
  'Racing Bulls':     '#6692FF',
  'Audi':             '#52E252',
  'Haas':             '#B6BABD',
  'Haas F1 Team':     '#B6BABD',
  'Cadillac':         '#CC0000',
};

const teamColor = (team) => TEAM_COLORS[team] || '#e10600';

const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
};

const RANK_STYLE = {
  1: { bg: '#F59E0B', border: 'rgba(245,158,11,0.55)' },
  2: { bg: '#9CA3AF', border: 'rgba(156,163,175,0.55)' },
  3: { bg: '#B45309', border: 'rgba(180,83,9,0.55)' },
};

const DriverCard = ({ driver, selected, onClick, disabled, badge }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const color = teamColor(driver.team);
  const rankInfo = badge != null ? RANK_STYLE[badge] : null;
  const activeBorder = rankInfo ? rankInfo.bg : '#e10600';

  return (
    <button
      type="button"
      onClick={() => !disabled && onClick(driver.driverId)}
      disabled={disabled}
      className={`relative flex flex-col rounded-xl border-2 overflow-hidden transition-all duration-150 text-left
        ${disabled && !selected ? 'opacity-35 cursor-not-allowed' : 'cursor-pointer'}
        ${selected ? 'scale-[1.03]' : 'hover:border-zinc-600'}`}
      style={{
        borderColor: selected ? activeBorder : 'rgb(39,39,42)',
        background: selected ? `${activeBorder}18` : 'rgb(24,24,27)',
      }}
    >
      <div className="w-full h-24 overflow-hidden relative"
        style={{ background: `linear-gradient(to bottom, ${color}22, ${color}08)` }}>
        {driver.headshot && !imgFailed ? (
          <img
            src={driver.headshot}
            alt={driver.familyName}
            className="w-full h-full object-cover object-top"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-3xl font-black select-none" style={{ color: `${color}99` }}>
              {driver.code || '?'}
            </span>
          </div>
        )}
        {selected && (
          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center shadow-lg"
            style={{ background: activeBorder }}>
            {badge === 1 ? (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 3h12M6 3v4a6 6 0 0012 0V3M6 3H4M18 3h2M9 17v2m6-2v2M7 19h10" />
              </svg>
            ) : badge === 2 || badge === 3 ? (
              <span className="text-white text-[10px] font-black">{badge}</span>
            ) : (
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        )}
      </div>
      <div className="px-2 py-1.5 w-full" style={{ borderTop: `1px solid ${color}33` }}>
        <p className="text-white text-[11px] font-black leading-tight truncate">{driver.familyName}</p>
        <p className="text-[9px] font-semibold mt-0.5 truncate" style={{ color }}>#{driver.permanentNumber} · {driver.team}</p>
      </div>
    </button>
  );
};

export const PodiumPicker = ({ winner, p2, p3, onChange, drivers, disabled }) => {
  const [search, setSearch] = useState('');

  const getRank = (driverId) => {
    if (winner === driverId) return 1;
    if (p2 === driverId) return 2;
    if (p3 === driverId) return 3;
    return null;
  };

  const filledCount = [winner, p2, p3].filter(Boolean).length;

  const handleClick = (driverId) => {
    if (disabled) return;
    const rank = getRank(driverId);
    if (rank !== null) {
      const remaining = [winner, p2, p3].filter(d => d && d !== driverId);
      onChange({ winner: remaining[0] || '', p2: remaining[1] || '', p3: remaining[2] || '' });
    } else if (filledCount < 3) {
      if (!winner) onChange({ winner: driverId, p2, p3 });
      else if (!p2) onChange({ winner, p2: driverId, p3 });
      else onChange({ winner, p2, p3: driverId });
    }
  };

  const filtered = drivers.filter(d => {
    const q = search.toLowerCase();
    return (
      d.familyName?.toLowerCase().includes(q) ||
      d.givenName?.toLowerCase().includes(q) ||
      d.team?.toLowerCase().includes(q) ||
      d.code?.toLowerCase().includes(q) ||
      String(d.permanentNumber).includes(q)
    );
  });

  const SLOTS = [
    { rank: 1, key: 'winner', label: 'Vainqueur', color: '#F59E0B', val: winner },
    { rank: 2, key: 'p2',     label: '2e place',  color: '#9CA3AF', val: p2 },
    { rank: 3, key: 'p3',     label: '3e place',  color: '#B45309', val: p3 },
  ];
  const getDriver = (id) => drivers.find(d => d.driverId === id);
  const hints = ['Clique sur le vainqueur', 'Clique sur le 2e', 'Clique sur le 3e'];

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-2">
        {SLOTS.map(slot => {
          const drv = getDriver(slot.val);
          return (
            <div key={slot.rank} className="rounded-xl border p-2.5 text-center transition-all"
              style={{ borderColor: drv ? slot.color + '55' : 'rgb(39,39,42)', background: drv ? slot.color + '10' : undefined }}>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: slot.color }}>{slot.label}</p>
              {drv
                ? <p className="text-white text-xs font-black truncate">{drv.familyName}</p>
                : <p className="text-zinc-600 text-xs">—</p>}
            </div>
          );
        })}
      </div>

      {!disabled && filledCount < 3 && (
        <p className="text-xs text-zinc-500 italic">{hints[filledCount]}</p>
      )}

      <input
        type="text"
        className="input text-sm py-1.5"
        placeholder="Rechercher un pilote..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        disabled={disabled}
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[480px] overflow-y-auto pr-0.5">
        {filtered.map(d => {
          const rank = getRank(d.driverId);
          return (
            <DriverCard
              key={d.driverId}
              driver={d}
              selected={rank !== null}
              onClick={handleClick}
              disabled={disabled || (filledCount >= 3 && rank === null)}
              badge={rank}
            />
          );
        })}
        {filtered.length === 0 && (
          <p className="col-span-4 text-zinc-500 text-sm text-center py-6">Aucun pilote trouvé</p>
        )}
      </div>
    </div>
  );
};

const DriverCardPicker = ({ label, value, onChange, drivers, disabled, multi = false, max = null, disabledIds = [] }) => {
  const [search, setSearch] = useState('');

  const filtered = drivers.filter(d => {
    const q = search.toLowerCase();
    return (
      d.familyName?.toLowerCase().includes(q) ||
      d.givenName?.toLowerCase().includes(q) ||
      d.team?.toLowerCase().includes(q) ||
      d.code?.toLowerCase().includes(q) ||
      String(d.permanentNumber).includes(q)
    );
  });

  const handleClick = (driverId) => {
    if (multi) {
      const arr = Array.isArray(value) ? value : [];
      if (arr.includes(driverId)) {
        onChange(arr.filter(d => d !== driverId));
      } else if (max === null || arr.length < max) {
        onChange([...arr, driverId]);
      }
    } else {
      onChange(value === driverId ? '' : driverId);
    }
  };

  const isSelected = (driverId) =>
    multi ? (Array.isArray(value) && value.includes(driverId)) : value === driverId;

  const selectedCount = multi ? (Array.isArray(value) ? value.length : 0) : 0;
  const isMaxed = multi && max !== null && selectedCount >= max;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider">
          {label}
          {max !== null && <span className="ml-1 font-normal normal-case text-zinc-600">({selectedCount}/{max})</span>}
        </label>
        {multi && selectedCount > 0 && (
          <span className="text-xs text-f1red font-semibold">{selectedCount} sélectionné{selectedCount > 1 ? 's' : ''}</span>
        )}
        {!multi && value && (
          <button type="button" onClick={() => onChange('')} className="text-xs text-zinc-500 hover:text-zinc-300">
            ✕ Effacer
          </button>
        )}
      </div>

      <input
        type="text"
        className="input text-sm py-1.5"
        placeholder="Rechercher par nom, écurie ou numéro..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        disabled={disabled}
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[480px] overflow-y-auto pr-0.5">
        {filtered.map(d => (
          <DriverCard
            key={d.driverId}
            driver={d}
            selected={isSelected(d.driverId)}
            onClick={handleClick}
            disabled={disabled || disabledIds.includes(d.driverId) || (isMaxed && !isSelected(d.driverId))}
          />
        ))}
        {filtered.length === 0 && (
          <p className="col-span-4 text-zinc-500 text-sm text-center py-6">Aucun pilote trouvé</p>
        )}
      </div>
    </div>
  );
};

export default DriverCardPicker;
